/**
 * Industry (trade vertical) pages.
 *
 * Why these exist: research into what a new agency domain can realistically
 * rank for found that service-by-trade queries — "google ads for plumbers",
 * "marketing for HVAC contractors" — are contested by far fewer established
 * sites than head terms like "digital marketing agency", and that agency
 * sites currently ranking for them do so with a dedicated page per trade.
 *
 * The same research flagged the opposite conclusion for geography: cloned
 * city pages are the single highest scaled-content-abuse risk for a site of
 * this size. So the site holds at four city pages and expands by vertical
 * instead, which is where the distinct, genuinely useful content is.
 *
 * CONTENT RULE: a trade page only belongs here if there is something true and
 * specific to say about how marketing that trade differs — its demand cycle,
 * its buying urgency, its margin structure, what a lead is worth. A page that
 * is another trade's page with the noun swapped is a doorway page, which is
 * named in Google's spam policies. If a trade cannot be written about
 * specifically, it does not get a page.
 *
 * FIGURES: only cite numbers that trace to a real source. The drain and
 * plumbing figures below come from the Pro Drain Cleaning account and from
 * the benchmark studies named inline. Where no sourced figure exists for a
 * trade, the page describes the dynamics qualitatively rather than inventing
 * a number.
 */

export interface IndustryFAQ {
  question: string;
  answer: string;
}

export interface IndustryDefinition {
  slug: string;
  trade: string;
  navLabel: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  /** 40-60 word self-contained opener — the extraction target. */
  directAnswer: string;
  cardSummary: string;
  keywords: string[];
  /** What makes marketing this trade different. Must be trade-specific. */
  whatMakesItDifferent: { heading: string; body: string }[];
  /** The searches that actually convert for this trade. */
  searchBehaviour: string[];
  /** Mistakes specific to this trade's campaigns. */
  commonMistakes: string[];
  faqs: IndustryFAQ[];
  priorityServices: string[];
  /** Slug of a case study in this vertical, if one exists. Rendered as a proof callout. */
  caseStudySlug?: string;
  /** Blog guides specific to this trade, for two-way internal linking. */
  relatedGuides?: { slug: string; label: string }[];
}

export const INDUSTRIES: IndustryDefinition[] = [
  {
    slug: 'plumbers',
    trade: 'Plumbing',
    navLabel: 'Plumbers',
    metaTitle: 'Google Ads & Marketing for Plumbers',
    metaDescription:
      'Marketing for plumbing companies — emergency search behaviour, call tracking that counts real jobs, and why cheap clicks are the wrong target in this trade.',
    h1: 'Marketing for Plumbing Companies',
    directAnswer:
      'Plumbing is an emergency-led trade: most high-value jobs begin with someone searching on a phone with water on the floor. That makes speed of response, call tracking and after-hours availability worth more than ad copy. Campaigns that optimise for cheap clicks in this trade consistently lose money.',
    cardSummary: 'Emergency-led demand where response speed beats ad copy.',
    keywords: [
      'google ads for plumbers',
      'marketing for plumbing companies',
      'plumber ppc management',
      'plumbing seo services',
      'lead generation for plumbers',
      'emergency plumber advertising',
    ],
    whatMakesItDifferent: [
      {
        heading: 'The job is urgent, so the lead is perishable',
        body: 'A burst pipe is not researched over a week. The customer calls the first company that answers and can attend. Published research on lead response consistently finds that responding within the first minute converts dramatically better than responding within thirty, and in home services the first responder wins the large majority of jobs. This matters more than any campaign setting: an account producing good leads into a business that returns calls in batches the next morning will look like a failing account.',
      },
      {
        heading: 'Cost per click is high because the jobs are worth a lot',
        body: 'Emergency plumbing keywords are among the most expensive in local search. Google’s own benchmark data for Winnipeg plumbing and drain work puts typical cost per click in the $45–85 CAD range, with emergency terms higher. That looks alarming until it is set against job value — a main-line repair or a burst-pipe callout usually justifies it. The trade’s problem is almost never that clicks cost too much; it is that the cheap clicks get counted as success.',
      },
      {
        heading: 'Most leads arrive by phone, so form tracking measures nothing',
        body: 'In urgent categories the overwhelming majority of enquiries are calls. An account tracking only form submissions is blind to most of its conversions, which means the bidding algorithm is optimising toward the wrong keywords. Call tracking is not an optional refinement for a plumbing account — without it the data is not merely incomplete, it is misleading.',
      },
    ],
    searchBehaviour: [
      'Emergency intent — "burst pipe", "no hot water", "sewage backup" — converts highest and costs most',
      'Problem-named searches outperform service-named ones: people search their symptom, not your service list',
      'Near-me and neighbourhood modifiers dominate mobile searches',
      '"24 hour" and "open now" qualifiers signal the highest-value, least price-sensitive jobs',
      'Informational searches ("how to unblock a drain") are DIY intent and belong in negatives, not targets',
    ],
    commonMistakes: [
      'Broad match on emergency terms, which pulls in DIY and parts-shopping searches at emergency prices',
      'Tracking form fills only, when most leads call',
      'Running 24/7 ads without 24/7 answering — paying premium rates for calls nobody picks up',
      'Sending emergency clicks to a homepage instead of a page with a tappable number above the fold',
      'Bidding city-wide when the van cannot profitably cross the city',
    ],
    faqs: [
      {
        question: 'How much do Google Ads cost for plumbers?',
        answer:
          'Plumbing is at the expensive end of local search. Google’s benchmark data for Winnipeg plumbing and drain work indicates roughly $45–85 CAD per click, with emergency terms higher, and costs vary widely by city. The number that matters is cost per booked job, not cost per click.',
      },
      {
        question: 'What is a realistic cost per lead for a plumbing company?',
        answer:
          'Industry benchmark research on drain and sewer work puts national averages around $166–231 in CAD-equivalent terms, with well-optimised campaigns averaging roughly $78–141. On the Pro Drain Cleaning account we brought cost per real lead from $716.65 to $90.23. Anyone promising $25 leads in this trade is either counting wrong numbers or guessing.',
      },
      {
        question: 'Do I need call tracking for plumbing ads?',
        answer:
          'Yes, and it is not optional. Most plumbing leads arrive by phone. An account measuring only form submissions is optimising on a small and unrepresentative slice of its conversions, which steadily pushes budget toward the wrong keywords.',
      },
      {
        question: 'Should plumbers run ads overnight?',
        answer:
          'Only if someone genuinely answers overnight. Night-time emergency searches are the most valuable in the trade, but paying premium rates for calls that go to voicemail wastes the budget and hands the job to the competitor who picked up. If you do not answer at 3am, schedule around it.',
      },
      {
        question: 'Is SEO or Google Ads better for a plumbing business?',
        answer:
          'Ads capture emergencies today; SEO compounds but takes months. Most plumbing companies need ads for immediate work while local SEO and the Google Business Profile build underneath. The map pack handles a large share of local plumbing searches, so the profile usually deserves attention before SEO content does.',
      },
    ],
    priorityServices: ['google-ads-management', 'conversion-rate-optimization', 'seo'],
    relatedGuides: [
      { slug: 'seo-for-plumbers', label: 'SEO for plumbers: how plumbing companies actually get found on Google' },
      { slug: 'google-ads-cost-per-lead-home-services', label: 'What a lead actually costs on Google Ads' },
      { slug: 'ai-search-for-home-service-businesses', label: 'How home service businesses get recommended by ChatGPT and Google AI' },
    ],
  },
  {
    slug: 'hvac-contractors',
    trade: 'HVAC',
    navLabel: 'HVAC Contractors',
    metaTitle: 'Google Ads & Marketing for HVAC Contractors',
    metaDescription:
      'Marketing for HVAC companies — seasonal demand swings, high-ticket replacement jobs, and how to budget for a trade where demand doubles with the weather.',
    h1: 'Marketing for HVAC Contractors',
    directAnswer:
      'HVAC demand is driven by weather, not by steady year-round need. Search volume spikes in the first heatwave and the first freeze, and collapses between them. Campaigns budgeted evenly across the year underspend exactly when the valuable jobs appear and waste money in the shoulder seasons.',
    cardSummary: 'Weather-driven demand where budget timing decides the year.',
    keywords: [
      'google ads for hvac contractors',
      'hvac marketing agency',
      'hvac lead generation',
      'air conditioning advertising',
      'furnace repair marketing',
      'hvac ppc management',
    ],
    whatMakesItDifferent: [
      {
        heading: 'Demand is weather-triggered, not seasonal in the usual sense',
        body: 'HVAC is not simply "busy in summer". Demand spikes within days of the first serious heatwave and the first hard freeze, and those spikes can produce more qualified search volume in a week than the preceding month. A flat monthly budget guarantees you are underspent during the only periods that matter. Budget in this trade should be held back deliberately so it can be deployed when the weather turns.',
      },
      {
        heading: 'Two completely different jobs share one account',
        body: 'A $200 service call and a $9,000 system replacement are different businesses with different search behaviour, different sales cycles and wildly different values. Replacement searches involve comparison, financing questions and multiple quotes; repair searches are urgent and immediate. Running them in one undifferentiated campaign means bidding the same amount for a lead worth forty times less.',
      },
      {
        heading: 'Maintenance plans change what a lead is worth',
        body: 'Contractors selling maintenance agreements can justify far higher acquisition costs, because the first job is the start of a recurring relationship rather than a one-off. If maintenance plans are part of the business, the cost-per-lead target should be calculated on customer lifetime value, not on the first invoice — and most HVAC accounts are budgeted as though that relationship does not exist.',
      },
    ],
    searchBehaviour: [
      'Repair intent spikes hard with weather events and converts within hours',
      'Replacement intent is researched over days or weeks and involves multiple quotes',
      'Financing and cost queries indicate replacement intent, not time-wasting',
      'Brand-name searches (specific manufacturers) signal a customer already partway to a decision',
      'Off-season searches skew toward maintenance and inspection, at much lower cost per click',
    ],
    commonMistakes: [
      'Spreading budget evenly across the year instead of concentrating it on weather events',
      'Mixing repair and replacement keywords in one ad group at one bid',
      'Pausing campaigns entirely in shoulder season, which resets Smart Bidding learning before peak',
      'Ignoring maintenance-plan lifetime value when setting cost-per-lead targets',
      'No capacity plan for peak — generating leads the team cannot physically attend',
    ],
    faqs: [
      {
        question: 'When should an HVAC company increase ad spend?',
        answer:
          'Ahead of and during weather events, not evenly across the year. The first heatwave and the first hard freeze produce the sharpest spikes in qualified search volume. Holding budget back through mild weather so it can be deployed when demand appears generally outperforms a flat monthly spend.',
      },
      {
        question: 'Should repair and replacement campaigns be separate?',
        answer:
          'Yes. A service call and a full system replacement differ in value by a large multiple and involve completely different search behaviour. Running them together forces one bid across leads worth very different amounts, which systematically overpays for the cheap ones and underbids on the valuable ones.',
      },
      {
        question: 'What should HVAC contractors do in the off season?',
        answer:
          'Reduce rather than pause. Pausing a campaign entirely resets Smart Bidding’s learning, so it arrives at peak season with no recent data at exactly the wrong moment. Shoulder season is also when maintenance and inspection keywords are cheapest and the Google Business Profile and site work can be done without competing for attention.',
      },
      {
        question: 'How do maintenance plans affect what a lead is worth?',
        answer:
          'Substantially. If a first job typically converts into a recurring agreement, acquisition cost should be judged against the relationship rather than the first invoice. Most HVAC accounts are budgeted as though every job is one-off, which sets the cost-per-lead ceiling far too low.',
      },
    ],
    priorityServices: ['google-ads-management', 'seo', 'conversion-rate-optimization'],
    relatedGuides: [
      { slug: 'ai-search-for-home-service-businesses', label: 'How home service businesses get recommended by ChatGPT and Google AI' },
    ],
  },
  {
    slug: 'roofing-companies',
    trade: 'Roofing',
    navLabel: 'Roofing Companies',
    metaTitle: 'Google Ads & Marketing for Roofing Companies',
    metaDescription:
      'Marketing for roofers — storm-driven demand, insurance claim work, long consideration cycles, and why roofing lead costs look alarming until you see job value.',
    h1: 'Marketing for Roofing Companies',
    directAnswer:
      'Roofing combines very high job values with long consideration periods and storm-driven demand spikes. Lead costs in this trade routinely look alarming in isolation and are often perfectly sound against a job worth five figures. The real risk is not expensive leads but leads that never get followed up.',
    cardSummary: 'High job values, long decisions, storm-driven spikes.',
    keywords: [
      'google ads for roofers',
      'roofing marketing agency',
      'roofing lead generation',
      'roof replacement advertising',
      'storm damage roofing marketing',
      'roofing contractor seo',
    ],
    whatMakesItDifferent: [
      {
        heading: 'Job values justify lead costs that look absurd elsewhere',
        body: 'A roof replacement is frequently a five-figure job. Against that, a lead costing several hundred is defensible in a way it never would be for a service call. The arithmetic that matters is job value multiplied by close rate, and roofing contractors routinely under-invest because they benchmark their cost per lead against trades with completely different economics.',
      },
      {
        heading: 'Storms create demand spikes and attract transient competition',
        body: 'Severe weather produces a surge in roofing searches within days. It also attracts out-of-area operators who descend on affected regions, bid aggressively and leave. During these windows, cost per click rises sharply and the competitive set changes overnight. Established local contractors win by being visibly local and established — which is a landing-page and profile problem, not a bidding one.',
      },
      {
        heading: 'Insurance work is a separate funnel with its own language',
        body: 'Customers with storm damage search in insurance terms — claims, adjusters, coverage — not in roofing terms. This is a distinct keyword set and a distinct page, and contractors who handle claim paperwork have a real advantage worth stating explicitly. Treating insurance intent as generic roofing intent misses the searches and the differentiator at once.',
      },
    ],
    searchBehaviour: [
      'Replacement searches are researched over weeks, with several quotes gathered',
      'Storm and leak searches are urgent and convert fast',
      'Insurance-claim phrasing is a separate, high-value keyword set',
      'Material-specific searches (metal, shingle, flat) indicate an informed buyer further along',
      'Financing queries signal intent rather than inability to pay',
    ],
    commonMistakes: [
      'Judging cost per lead against trades with far lower job values',
      'No follow-up sequence for leads that take weeks to decide — the most expensive error in this trade',
      'Ignoring insurance-claim keywords and the claim-handling differentiator',
      'Competing on price against transient post-storm operators instead of on being locally established',
      'Sending every click to a generic homepage regardless of whether intent was repair, replacement or claim',
    ],
    faqs: [
      {
        question: 'Why is cost per lead so high for roofing?',
        answer:
          'Because job values are high and every competitor knows it. A roof replacement is often a five-figure job, so a lead costing several hundred can still be very profitable. Benchmarking roofing lead costs against trades with much smaller jobs produces the wrong conclusion.',
      },
      {
        question: 'How should roofers handle demand after a storm?',
        answer:
          'Expect a sharp spike in searches, higher click costs, and an influx of out-of-area operators bidding aggressively. Being visibly local and established is the strongest differentiator in that window, which is a landing-page and profile question more than a bidding one.',
      },
      {
        question: 'Should roofing companies target insurance claim keywords?',
        answer:
          'If you handle claim paperwork, yes. Customers with storm damage search in insurance language rather than roofing language, so these are different keywords needing a different page. Claim handling is also a genuine differentiator worth stating plainly where those searchers land.',
      },
      {
        question: 'Why do roofing leads go cold?',
        answer:
          'Because roof replacement is usually considered over weeks while most contractors follow up for days. The highest-return fix in most roofing accounts is not in the campaign at all — it is a follow-up sequence that persists through the actual length of the decision.',
      },
    ],
    priorityServices: ['google-ads-management', 'web-design-development', 'conversion-rate-optimization'],
    relatedGuides: [
      { slug: 'ai-search-for-home-service-businesses', label: 'How home service businesses get recommended by ChatGPT and Google AI' },
    ],
  },
  {
    slug: 'drain-cleaning',
    trade: 'Drain & Sewer',
    navLabel: 'Drain & Sewer',
    metaTitle: 'Google Ads & Marketing for Drain Companies',
    metaDescription:
      'Marketing for drain and sewer companies — the trade where we have documented results, including cost per lead reduced from $716.65 to $90.23.',
    h1: 'Marketing for Drain and Sewer Companies',
    directAnswer:
      'Drain and sewer work is the trade where WeBuildPro has the most documented evidence. On the Pro Drain Cleaning account in Winnipeg, restructuring campaigns took click-through rate from 1.40% to 8.01% and cost per real lead from $716.65 to $90.23 against the setup inherited from the previous provider.',
    cardSummary: 'Our most documented vertical — with the numbers to show.',
    keywords: [
      'google ads for drain cleaning',
      'drain cleaning marketing',
      'sewer company advertising',
      'drain cleaning lead generation',
      'drain cleaning seo',
      'plumbing drain ppc',
    ],
    whatMakesItDifferent: [
      {
        heading: 'What we actually changed on a real drain account',
        body: 'Pro Drain Cleaning came to us with campaigns built by a previous provider. The restructure used phrase and exact match in place of broad, negative keyword lists built from the real search terms report, conversion tracking rewired to count calls and form submissions rather than page views, and ad scheduling weighted to when emergency searches actually happen. Click-through rate went from 1.40% to 8.01%, real conversion rate from 0.97% to 10.63%, and cost per real lead from $716.65 to $90.23.',
      },
      {
        heading: 'The benchmark context most contractors are never shown',
        body: 'Independent benchmark research on drain and sewer advertising puts national average cost per lead at roughly $166–231 in CAD-equivalent terms, with campaigns described as well-optimised averaging around $78–141. Google’s own benchmark data for Winnipeg plumbing and drain work indicates $95–190 CAD typical and $140–220+ for emergency terms. Contractors who have been told to expect $25 leads are comparing against a number that does not exist in this trade.',
      },
      {
        heading: 'Weather and season move this trade sharply',
        body: 'In cold climates, freeze and thaw cycles drive the demand. Deep freezes produce frozen and burst pipe calls; spring thaw produces drain backups and basement flooding. These are not gradual seasonal curves — volume spikes within days of a weather event, which rewards campaigns able to flex budget rather than spend it evenly.',
      },
    ],
    searchBehaviour: [
      'Backup and flooding searches are the most urgent and the most valuable',
      'Camera inspection and line-locating searches indicate a diagnosed, higher-value job',
      'Commercial and restaurant drain searches convert at higher values than residential',
      'DIY searches ("how to unblock a drain") are a negative keyword set, not a target',
      'Competitor brand searches are worth negative-listing rather than bidding on',
    ],
    commonMistakes: [
      'Counting page views or every phone-link tap as a conversion, which inflates results and misdirects bidding',
      'Broad match pulling DIY and parts searches into an emergency-priced account',
      'Spending evenly through the year in a trade driven by freeze and thaw events',
      'No commercial keywords despite the business taking commercial work',
      'Leaving the Google Business Profile unverified while paying for clicks',
    ],
    faqs: [
      {
        question: 'What results have you achieved for a drain company?',
        answer:
          'On the Pro Drain Cleaning account in Winnipeg, comparing campaigns we built against those inherited from the previous provider: click-through rate rose from 1.40% to 8.01%, real conversion rate from 0.97% to 10.63%, and cost per real lead fell from $716.65 to $90.23. Those figures come from the client’s own Google Ads account.',
      },
      {
        question: 'What is a realistic cost per lead for drain cleaning?',
        answer:
          'Benchmark research puts national averages around $166–231 in CAD-equivalent terms, with well-optimised campaigns averaging roughly $78–141. Local market and urgency move this considerably. Targets substantially below that range are generally built on counting something other than real leads.',
      },
      {
        question: 'Why do my drain ads show conversions but no jobs?',
        answer:
          'Almost always because the conversion action is not a real lead. Page views of a contact page, or every tap on a phone link whether or not a call connects, both inflate the count. Once bidding optimises toward those, it finds more of the people who do them and fewer who actually call.',
      },
      {
        question: 'Does seasonality matter for drain companies?',
        answer:
          'In cold climates, enormously. Freeze events drive frozen and burst pipe calls and thaw drives backups and basement flooding, both spiking within days rather than building gradually. Campaigns that can concentrate budget on those windows outperform those spending evenly.',
      },
    ],
    priorityServices: ['google-ads-management', 'conversion-rate-optimization', 'web-design-development'],
    caseStudySlug: 'pro-drain-cleaning-google-ads',
    relatedGuides: [
      { slug: 'google-ads-cost-per-lead-home-services', label: 'What a lead actually costs on Google Ads' },
      { slug: 'seo-for-plumbers', label: 'SEO for plumbing and drain companies' },
      { slug: 'ai-search-for-home-service-businesses', label: 'How home service businesses get recommended by ChatGPT and Google AI' },
    ],
  },
  {
    slug: 'electricians',
    trade: 'Electrical',
    navLabel: 'Electricians',
    metaTitle: 'Google Ads & Marketing for Electricians',
    metaDescription:
      'Marketing for electrical contractors — mixed emergency and project work, EV charger and panel upgrade demand, and licence trust signals that drive conversion.',
    h1: 'Marketing for Electrical Contractors',
    directAnswer:
      'Electrical work splits between urgent faults and planned projects, and the two behave nothing alike. Fault searches convert within hours; panel upgrades, rewires and EV charger installs are researched and quoted. Treating them as one audience is the most common structural error in electrical accounts.',
    cardSummary: 'Split between urgent faults and planned, quoted projects.',
    keywords: [
      'google ads for electricians',
      'electrician marketing agency',
      'electrical contractor leads',
      'ev charger installation marketing',
      'electrician seo services',
      'electrical ppc management',
    ],
    whatMakesItDifferent: [
      {
        heading: 'Two audiences with nothing in common',
        body: 'A tripping breaker at 9pm and a planned kitchen rewire are different purchases. The first converts in hours on urgency and availability; the second involves quotes, scheduling around other trades, and sometimes a renovation timeline measured in months. One campaign cannot serve both well — the bids, the ad copy, the landing page and the follow-up all need to differ.',
      },
      {
        heading: 'Licensing and insurance are conversion levers, not fine print',
        body: 'Electrical work carries real safety and legal weight, and customers know it. Licence numbers, insurance details and certifications displayed prominently measurably reduce hesitation, in a way that is much less true for trades where the downside of hiring badly is inconvenience rather than fire. Burying these in a footer wastes the strongest trust signal the trade has.',
      },
      {
        heading: 'EV chargers and panel upgrades are a growing, less contested segment',
        body: 'Electrification is producing steady demand for EV charger installation, panel upgrades and load assessments. These searches are newer, often less contested than general electrician terms, and attach to high-value work. For a contractor who does them, they are usually a better early target than competing on the generic trade term.',
      },
    ],
    searchBehaviour: [
      'Fault and outage searches are urgent and convert within hours',
      'Panel upgrade, rewire and EV charger searches involve quotes and comparison',
      'Code compliance and inspection queries signal commercial or property-sale work',
      'Smart home and lighting searches skew toward discretionary, higher-margin projects',
      'Commercial electrical searches carry far higher contract values than residential',
    ],
    commonMistakes: [
      'One campaign serving both emergency faults and planned projects',
      'Licence and insurance details buried in the footer rather than placed where decisions happen',
      'No separate targeting for EV charger and panel upgrade demand',
      'Treating quote requests as lost when they do not close immediately',
      'Residential-framed ad copy shown to commercial searches',
    ],
    faqs: [
      {
        question: 'Should electricians separate emergency and project campaigns?',
        answer:
          'Yes. An urgent fault and a planned rewire differ in urgency, value and decision length. They need different bids, different ad copy, different landing pages and different follow-up. Combining them produces a campaign that serves neither properly.',
      },
      {
        question: 'Are EV charger installation searches worth targeting?',
        answer:
          'For contractors who do the work, usually yes. The searches are newer and often less contested than general electrician terms while attaching to high-value jobs. For a newer business, that is frequently a better entry point than competing head-on for the generic trade term.',
      },
      {
        question: 'How much do licence and insurance details matter?',
        answer:
          'More than in most trades. Customers understand that bad electrical work is dangerous, so visible licence numbers, insurance and certifications measurably reduce hesitation. Displaying them where decisions happen rather than in the footer is one of the cheapest conversion improvements available.',
      },
      {
        question: 'Why do electrical quote requests not convert?',
        answer:
          'Often because project work runs on a longer timeline than the follow-up does. A kitchen rewire may wait on other trades for weeks. Contractors who keep following up through that period close work that competitors have already written off.',
      },
    ],
    priorityServices: ['google-ads-management', 'seo', 'web-design-development'],
    relatedGuides: [
      { slug: 'ai-search-for-home-service-businesses', label: 'How home service businesses get recommended by ChatGPT and Google AI' },
    ],
  },
  {
    slug: 'cleaning-companies',
    trade: 'Cleaning',
    navLabel: 'Cleaning Companies',
    metaTitle: 'Google Ads & Marketing for Cleaning Companies',
    metaDescription:
      'Marketing for cleaning businesses — recurring revenue changes the maths entirely, and commercial contracts behave nothing like residential bookings.',
    h1: 'Marketing for Cleaning Companies',
    directAnswer:
      'Cleaning is the trade where lifetime value matters most. A recurring residential client books for years, so a lead costing far more than the first clean can still be highly profitable. Cleaning businesses that budget against the first invoice systematically underinvest and lose to competitors who do the lifetime calculation.',
    cardSummary: 'Recurring revenue changes what a lead is worth.',
    keywords: [
      'google ads for cleaning companies',
      'cleaning business marketing',
      'commercial cleaning lead generation',
      'house cleaning advertising',
      'cleaning company seo',
      'janitorial marketing agency',
    ],
    whatMakesItDifferent: [
      {
        heading: 'Recurring revenue changes the arithmetic completely',
        body: 'A one-off clean might be worth $150. A fortnightly client retained for two years is worth several thousand. Judging acquisition cost against the first booking is the single most common and most expensive error in this trade: it caps spend far below what the business can profitably afford and hands the recurring clients to whoever did the arithmetic properly.',
      },
      {
        heading: 'Commercial and residential are separate businesses',
        body: 'Commercial cleaning involves contracts, procurement, site visits and decision cycles measured in months, with contract values an order of magnitude above residential. Residential books quickly, often on price and availability. The keywords, the ad copy, the landing page and the sales process share almost nothing. Running them together means one bid for leads worth vastly different amounts.',
      },
      {
        heading: 'Low barriers to entry make differentiation the hard part',
        body: 'Anyone can start a cleaning business, so the search results are crowded with competitors making identical claims. Insurance, bonding, police-checked staff, guarantees and a real booking process are the differentiators that survive comparison. Price competition in this trade is a race nobody wins.',
      },
    ],
    searchBehaviour: [
      'Residential searches convert quickly and skew heavily toward price and availability',
      'Move-out and end-of-tenancy cleans are urgent, deadline-driven and less price-sensitive',
      'Commercial and office searches carry much higher contract values and much longer cycles',
      'Specialist searches (post-construction, carpet, deep clean) attach to higher margins',
      'Recurring-service phrasing ("weekly", "fortnightly") signals the most valuable customers',
    ],
    commonMistakes: [
      'Setting cost-per-lead targets against the first clean rather than the retained relationship',
      'Mixing commercial and residential in one campaign at one bid',
      'Competing on price in a category where price competition has no floor',
      'No recurring-service offer in the ad or on the page, so one-off bookings never convert to contracts',
      'Ignoring deadline-driven end-of-tenancy searches, which are urgent and convert well',
    ],
    faqs: [
      {
        question: 'How should a cleaning company calculate cost per lead?',
        answer:
          'On the retained relationship, not the first booking. A fortnightly client kept for two years is worth many multiples of a single clean, so the cost that can be justified is far higher than the first invoice suggests. Businesses that budget on the first clean consistently underspend.',
      },
      {
        question: 'Should commercial and residential cleaning be marketed separately?',
        answer:
          'Yes, they are effectively different businesses. Commercial involves contracts, procurement and months-long cycles at much higher values; residential books fast on price and availability. Separate campaigns, pages and follow-up processes are needed for each.',
      },
      {
        question: 'How do cleaning companies compete when everyone looks the same?',
        answer:
          'Not on price, which has no floor in this category. Insurance, bonding, police-checked staff, a genuine guarantee and an easy booking process are the differentiators that hold up under comparison. Stating them specifically beats claiming to be reliable and professional.',
      },
      {
        question: 'Are end-of-tenancy cleaning searches worth targeting?',
        answer:
          'Usually yes. They are deadline-driven, which makes them urgent and comparatively less price-sensitive than routine residential searches, and they often lead to referrals from letting agents and landlords who book repeatedly.',
      },
    ],
    priorityServices: ['google-ads-management', 'meta-ads-management', 'conversion-rate-optimization'],
  },
];

export function getIndustry(slug: string): IndustryDefinition | undefined {
  return INDUSTRIES.find((industry) => industry.slug === slug);
}
